import { useState, useCallback, useEffect } from 'react';
import { Modal, Input, Select, TextArea, Toggle, RadioGroup, Button, useToast } from '@bbpos/ui';
import { ChannelPill } from './ChannelPill';
import type { CommTemplate, CreateTemplateInput, TemplateChannel, TemplateType, UpdateTemplateInput } from './types';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

const TEMPLATE_VARIABLES = [
  { key: '{{customer_name}}', label: 'Customer Name' },
  { key: '{{bill_number}}', label: 'Bill Number' },
  { key: '{{amount}}', label: 'Amount' },
  { key: '{{shop_name}}', label: 'Shop Name' },
  { key: '{{reorder_link}}', label: 'Reorder Link' },
];

const TYPE_OPTIONS = [
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'REORDER_LINK', label: 'Reorder Link' },
  { value: 'FEEDBACK_REQUEST', label: 'Feedback Request' },
];

interface TemplateEditorProps {
  open: boolean;
  onClose: () => void;
  template?: CommTemplate | null;
  onSave: (input: CreateTemplateInput | { id: string; input: UpdateTemplateInput }) => void;
  saving?: boolean;
}

export function TemplateEditor({ open, onClose, template, onSave, saving }: TemplateEditorProps) {
  const { toast } = useToast();
  const currencySymbol = getCurrencySymbol(useTenantCurrency());

  const [name, setName] = useState('');
  const [channel, setChannel] = useState<TemplateChannel>('WHATSAPP');
  const [type, setType] = useState<TemplateType>('INVOICE');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (open && template) {
      setName(template.name);
      setChannel(template.channel);
      setType(template.type);
      setBody(template.template_body);
      setIsDefault(template.is_default);
      setSubject('');
    } else if (open) {
      setName('');
      setChannel('WHATSAPP');
      setType('INVOICE');
      setSubject('');
      setBody('');
      setIsDefault(false);
    }
  }, [open, template]);

  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const insertVariable = useCallback((variable: string) => {
    setBody((prev) => prev + variable);
  }, []);

  function handleSubmit() {
    if (!name.trim()) {
      toast({ variant: 'warning', title: 'Name required', description: 'Please enter a template name.' });
      return;
    }
    if (!body.trim()) {
      toast({ variant: 'warning', title: 'Body required', description: 'Template body cannot be empty.' });
      return;
    }

    if (template) {
      onSave({
        id: template.id,
        input: {
          name: name.trim(),
          template_body: body,
          is_default: isDefault,
        },
      });
    } else {
      onSave({
        name: name.trim(),
        type,
        channel,
        subject: channel === 'EMAIL' && subject.trim() ? subject.trim() : undefined,
        body,
        is_default: isDefault,
      } as CreateTemplateInput);
    }
  }

  const previewBg = channel === 'WHATSAPP'
    ? 'bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20'
    : channel === 'EMAIL'
      ? 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
      : 'bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-700';

  return (
    <Modal open={open} onClose={onClose} title={template ? 'Edit Template' : 'New Template'} variant="wide">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form panel */}
        <div className="space-y-4">
          <Input
            label="Template Name"
            value={name}
            onChange={(v) => setName(v.slice(0, 100))}
            placeholder="e.g. Invoice - Default"
            required
          />

          {!template ? (
            <>
              <RadioGroup
                label="Channel"
                value={channel}
                onChange={(v) => setChannel(v as TemplateChannel)}
                variant="horizontal"
                options={[
                  { value: 'WHATSAPP', label: 'WhatsApp' },
                  { value: 'EMAIL', label: 'Email' },
                  { value: 'SMS', label: 'SMS' },
                ]}
              />

              <Select
                label="Type"
                options={TYPE_OPTIONS}
                value={type}
                onChange={(v) => setType(v as TemplateType)}
              />
            </>
          ) : (
            <div className="flex items-center gap-2">
              <ChannelPill channel={template.channel} size="md" />
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Channel and type are immutable after creation
              </span>
            </div>
          )}

          {channel === 'EMAIL' && !template ? (
            <Input
              label="Subject Line"
              value={subject}
              onChange={(v) => setSubject(v.slice(0, 200))}
              placeholder="Invoice #{{bill_number}} from {{shop_name}}"
            />
          ) : null}

          <div>
            <TextArea
              label="Template Body"
              value={body}
              onChange={setBody}
              placeholder={`Hi {{customer_name}}, your invoice of ${currencySymbol}{{amount}} is ready...`}
              maxLength={2000}
              rows={8}
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {TEMPLATE_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVariable(v.key)}
                  className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-800/40 transition-colors shadow-sm"
                >
                  + {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 p-4 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Default Template</p>
              <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                Used automatically when no template is selected
              </p>
            </div>
            <Toggle checked={isDefault} onChange={setIsDefault} />
          </div>
        </div>

        {/* Preview panel — Glass Depth with no borders */}
        <div className={`rounded-xl p-5 shadow-inner ${previewBg}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
            Live Preview
          </p>
          {channel === 'WHATSAPP' ? (
            /* Authentic WhatsApp-style chat bubble */
            <div className="rounded-2xl overflow-hidden shadow-lg max-w-xs mx-auto">
              {/* WhatsApp header bar */}
              <div className="bg-emerald-600 dark:bg-emerald-700 px-4 py-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-bold">
                  BB
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{name || 'Business Name'}</p>
                  <p className="text-emerald-200 text-[10px]">online</p>
                </div>
              </div>
              {/* Chat area with wallpaper effect */}
              <div className="bg-[#ECE5DD] dark:bg-neutral-800 p-4 min-h-[150px]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d5cec5\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}>
                <div className="flex justify-end mb-1">
                  <p className="text-[10px] text-neutral-500 font-mono tabular-nums">
                    {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-700 rounded-xl rounded-tr-sm p-3 shadow-sm max-w-[85%] ml-auto">
                  <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap break-words leading-relaxed">
                    {body || 'Template body will appear here...'}
                  </p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-[10px] text-neutral-400 font-mono tabular-nums">
                      {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <svg className="h-3.5 w-3.5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ) : channel === 'EMAIL' ? (
            <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden max-w-sm mx-auto">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-4">
                <p className="text-white text-xs font-semibold">{subject || 'Subject Line'}</p>
                <p className="text-teal-100 text-[10px] mt-0.5">{name || 'From: Business Name'}</p>
              </div>
              <div className="p-5 text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap break-words leading-relaxed min-h-[100px]">
                {body || 'Email body will appear here...'}
              </div>
              <div className="px-5 py-3 bg-neutral-50 dark:bg-neutral-700/30">
                <p className="text-[10px] text-neutral-400">Sent via Orio360</p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-lg p-5 max-w-xs mx-auto">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-6 w-6 rounded-full bg-neutral-300 dark:bg-neutral-600 flex items-center justify-center">
                  <svg className="h-3 w-3 text-neutral-500 dark:text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-wider">SMS Preview</p>
              </div>
              <div className="rounded-lg bg-neutral-50 dark:bg-neutral-700/40 p-3 text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap break-words leading-relaxed">
                {body || 'SMS body will appear here...'}
              </div>
              <p className="mt-2 text-[10px] text-neutral-400 font-mono tabular-nums text-right">
                {body.length}/160 chars
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} loading={saving}>
          {template ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </Modal>
  );
}
